import type { Dispatch, SetStateAction } from 'react';
import { Box, Checkbox, DropdownFilter, FormGroup } from '@ksyos/design-system';
import { useCallback, useMemo } from 'react';
import type { IEpisodeCategory } from '@common/interfaces';
import type { OutpatientClinicTaskListFilter } from '@frontend/components/pages/outpatientClinic/types/outpatientClinicTaskListFilter';
import { getSelectedLanguage } from '@frontend/translate';

export type EpisodeCategoryFilterProps = {
    episodeCategories: IEpisodeCategory[];
    filter: OutpatientClinicTaskListFilter;
    localEpisodeCategories: OutpatientClinicTaskListFilter['episodeCategory'];
    setFilter: (filter: OutpatientClinicTaskListFilter) => void;
    setLocalEpisodeCategories: Dispatch<SetStateAction<OutpatientClinicTaskListFilter['episodeCategory']>>;
};

export function EpisodeCategoryFilter({
    episodeCategories,
    filter,
    localEpisodeCategories,
    setFilter,
    setLocalEpisodeCategories,
}: EpisodeCategoryFilterProps) {
    const translate = getSelectedLanguage();

    const handleReset = useCallback(() => {
        setLocalEpisodeCategories(undefined);
        setFilter({ ...filter, episodeCategory: undefined });
    }, [filter, setLocalEpisodeCategories, setFilter]);

    const handleSubmit = useCallback(() => {
        setFilter({
            ...filter,
            episodeCategory: localEpisodeCategories,
        });
    }, [filter, localEpisodeCategories, setFilter]);

    const handleCheckboxChange = useCallback(
        (value: boolean, id: string) => {
            if (value) {
                setLocalEpisodeCategories([...(localEpisodeCategories || []), { id }]);
            } else {
                const filtered = (localEpisodeCategories || []).filter(
                    ({ id: localEpisodeCategoryId }) => localEpisodeCategoryId !== id
                );
                setLocalEpisodeCategories(filtered.length === 0 ? undefined : filtered);
            }
        },
        [localEpisodeCategories, setLocalEpisodeCategories]
    );

    const selectedEpisodeCategoryIds = useMemo(
        () => new Set(localEpisodeCategories?.map(({ id }) => id) ?? []),
        [localEpisodeCategories]
    );

    const episodeCategoryCheckboxes = useMemo(
        () =>
            episodeCategories?.map((episodeCategory) => (
                <Checkbox
                    checked={selectedEpisodeCategoryIds.has(episodeCategory.id)}
                    key={episodeCategory.id}
                    label={translate.episodeCategoryEnum[episodeCategory.name]}
                    onChange={(event) => handleCheckboxChange(event.target.checked, episodeCategory.id)}
                />
            )),
        [episodeCategories, handleCheckboxChange, selectedEpisodeCategoryIds, translate]
    );

    return (
        <FormGroup label={translate.common.episodeCategory}>
            <DropdownFilter
                count={filter.episodeCategory?.length}
                label={translate.common.filter}
                onReset={handleReset}
                onSubmit={handleSubmit}
                width="full"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                >
                    {episodeCategoryCheckboxes}
                </Box>
            </DropdownFilter>
        </FormGroup>
    );
}
